import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.45 },
};

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Latchiya Software Systems Pvt Ltd',
  url: 'https://latchiya.com',
  logo: 'https://latchiya.com/assets/images/latchiya_std.png',
  foundingDate: '2006',
  description:
    'Latchiya Software Systems: Documint (documint.id) as the flagship digital identity platform, plus m-REACH, eClassroomz, IDGate Check-In, Scan4Loyalty, and software consulting for enterprises in India.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1F-801, Divyasree Elan, Bellandur Gate, Sarjapur Main Road',
    addressLocality: 'Bangalore',
    postalCode: '560035',
    addressCountry: 'IN',
  },
  email: 'support@latchiya.com',
  telephone: '+91-9945101015',
};

const Home = () => {
  return (
    <>
      <SEOHead
        structuredData={structuredData}
        description="Documint—Latchiya's flagship product for digital identity innovation on documint.id. Self-sovereign identity, W3C Verifiable Credentials, DIDs, OpenID4VC. Also m-REACH, eClassroomz, IDGate Check-In, Scan4Loyalty wallet loyalty, consulting."
      />
      <div id="top">
        <section className="relative overflow-hidden border-b border-white/5 scroll-mt-24">
          <div
            className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary-600/25 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute top-20 -right-16 h-80 w-80 rounded-full bg-neon-500/15 blur-3xl"
            aria-hidden
          />
          <div className="container-custom relative py-16 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
              <motion.div {...fadeUp}>
                <a
                  href="https://documint.id/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mb-6 -m-1 p-1 rounded-xl hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                  aria-label="Documint — documint.id (opens in new tab)"
                >
                  <img
                    src="/assets/images/documint-logo.png"
                    alt="Documint"
                    className="h-8 sm:h-9 w-auto max-w-[min(100%,220px)] object-contain object-left"
                    width="220"
                    height="40"
                  />
                </a>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
                  The Future of Identity is instantly verifiable
                </h1>
                <p className="mt-5 text-lg text-gray-300 leading-relaxed max-w-xl">
                  <strong className="text-white font-semibold">Documint</strong>{' '}
                  is Latchiya&apos;s flagship product: where we invest our
                  deepest R&amp;D in the digital identity space—turning
                  credentials into cryptographically verifiable trust, not just
                  documents.
                </p>
                <p className="mt-4 text-sm text-gray-400 leading-relaxed max-w-xl">
                  Documint advances self-sovereign identity on modern
                  standards—W3C Verifiable Credentials, DIDs, and OpenID4VC—so
                  fraud-proof issuance and instant verification work for
                  universities, enterprises, and public ecosystems worldwide.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a
                    href="https://documint.id/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                  >
                    Open documint.id
                  </a>
                  <Link to="/contact" className="btn-secondary">
                    Contact Latchiya
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="glass-panel rounded-2xl p-6 lg:p-8 shadow-glow-strong"
              >
                <div className="flex items-start gap-3 mb-5">
                  <div
                    className="h-10 w-10 shrink-0 rounded-xl overflow-hidden ring-1 ring-white/15 shadow-sm"
                    aria-hidden
                  >
                    <img
                      src="/assets/images/documint-icon.png"
                      alt=""
                      className="h-full w-full object-cover"
                      width="40"
                      height="40"
                    />
                  </div>
                  <p className="text-sm font-medium text-neon-300 leading-snug pt-0.5">
                    Innovation in identity infrastructure
                  </p>
                </div>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li className="flex gap-3">
                    <span className="text-primary-400 shrink-0">✓</span>
                    <span>
                      <strong className="text-white">Standards-first</strong> —
                      W3C Verifiable Credentials and DIDs as the foundation for
                      portable, provable identity
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-400 shrink-0">✓</span>
                    <span>
                      <strong className="text-white">
                        Interoperable by design
                      </strong>{' '}
                      — OpenID4VC for issuance and verification across
                      ecosystems
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-400 shrink-0">✓</span>
                    <span>
                      <strong className="text-white">Trust at scale</strong> —
                      blockchain anchoring, instant verification, fraud
                      resistance, and privacy-preserving flows
                    </span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        <section
          id="idgate"
          className="py-16 lg:py-24 border-b border-white/5 bg-surface-200/30 scroll-mt-24"
        >
          <div className="container-custom">
            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              className="rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-950/35 to-surface-200/50 p-6 lg:p-10"
            >
              <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
                <div>
                  <div className="inline-flex max-w-[1100px] rounded-2xl bg-white px-4 py-1 shadow-lg shadow-black/20">
                    <img
                      src="/assets/images/idgate-logo.png"
                      alt="IDGate Check-In"
                      className="block h-60 w-auto object-contain object-left"
                      width="1100"
                      height="240"
                    />
                  </div>

                  <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mt-8">
                    Secure Check-In for Every High-Trust Entry Point
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-gray-300 leading-relaxed">
                    IDGate Check-In helps organizations verify identities,
                    streamline visitor movement, and strengthen front-desk
                    security through one modern platform. With real-time Aadhaar
                    verification, paperless registration, and centralized
                    monitoring, it delivers a faster, safer, and more
                    professional arrival experience for visitors, staff, and
                    contractors.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-primary text-sm px-6">
                      Schedule a Demo
                    </Link>
                    <a
                      href="mailto:support@latchiya.com?subject=IDGate%20%E2%80%94%20Sales%20inquiry"
                      className="btn-secondary text-sm px-6 text-center"
                    >
                      Talk to Sales
                    </a>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="glass-panel rounded-2xl p-6 lg:p-8 shadow-glow-strong lg:sticky lg:top-28"
                >
                  <p className="text-sm font-medium text-neon-300 mb-4">
                    Innovation in secure entry and visitor operations
                  </p>
                  <ul className="space-y-3 text-sm text-gray-300">
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">Identity-first</strong> —
                        Real-time Aadhaar verification and stronger assurance at
                        every check-in
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">
                          Frictionless arrival
                        </strong>{' '}
                        — Paperless registration, faster queues, and a polished
                        front-desk experience
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">
                          Governed &amp; visible
                        </strong>{' '}
                        — Centralized visitor views, audit trails, and
                        compliance-ready operational control
                      </span>
                    </li>
                  </ul>
                </motion.div>
              </div>

              <div className="mt-10 lg:mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  {
                    title: 'Real-Time Aadhaar Verification',
                    body: 'Confirm visitor identity instantly and reduce the risk of unauthorized access.',
                  },
                  {
                    title: 'Fast Digital Check-In',
                    body: 'Replace manual registers with a smooth, paperless entry flow that cuts wait times.',
                  },
                  {
                    title: 'Centralized Visitor Management',
                    body: 'Track visitors, contractors, and staff from a single dashboard with full visibility.',
                  },
                  {
                    title: 'Compliance-Ready Audit Trails',
                    body: 'Maintain secure records and access logs for accountability and operational control.',
                  },
                  {
                    title: 'Built for High-Footfall Operations',
                    body: 'Designed for environments where speed, trust, and security must work together.',
                  },
                ].map(({ title, body }) => (
                  <div
                    key={title}
                    className="rounded-xl border border-white/10 bg-surface-300/25 p-5"
                  >
                    <p className="font-display text-sm font-semibold text-white">
                      {title}
                    </p>
                    <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                      {body}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-10 border-t border-white/10">
                <p className="eyebrow mb-2">Verticals</p>
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Where IDGate Check-In Fits Best
                </h4>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-400 list-disc list-inside marker:text-primary-400">
                  {[
                    'Corporate offices',
                    'Hospitals and healthcare facilities',
                    'Manufacturing plants',
                    'Warehouses and logistics hubs',
                    'Educational campuses',
                    'Residential communities',
                    'Government and public sector facilities',
                    'Coworking spaces',
                    'Events and exhibitions',
                    'High-security commercial sites',
                  ].map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 rounded-xl border border-primary-500/25 bg-primary-950/30 p-6 lg:p-8">
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Confidence at Every Checkpoint
                </h4>
                <p className="mt-3 text-sm text-gray-300 leading-relaxed max-w-3xl">
                  Give every visitor a seamless welcome while giving your team
                  stronger control, better visibility, and dependable identity
                  verification.
                </p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-3 text-sm text-gray-400">
                  {[
                    'Faster entry processing',
                    'Stronger identity assurance',
                    'Reduced manual effort',
                    'Improved compliance readiness',
                  ].map(text => (
                    <li key={text} className="flex gap-2 items-start">
                      <span className="text-primary-400 shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          </div>
        </section>

        <section
          id="scan4loyalty"
          className="py-16 lg:py-24 border-b border-white/5 scroll-mt-24"
        >
          <div className="container-custom">
            <motion.article
              {...fadeUp}
              className="rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-950/35 to-surface-200/50 p-6 lg:p-10"
            >
              <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
                <div>
                  <div className="inline-flex rounded-2xl bg-white px-6 py-4 shadow-lg shadow-black/20">
                    <img
                      src="/assets/images/scan4loyalty-logo.png"
                      alt="Scan4Loyalty"
                      className="block h-28 sm:h-32 w-auto object-contain object-left"
                      width="900"
                      height="398"
                    />
                  </div>

                  <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mt-8">
                    Get More Repeat Customers. Automatically.
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-gray-300 leading-relaxed">
                    Scan4Loyalty turns the phone your customers already carry
                    into their loyalty card. Members add a pass to Apple Wallet
                    or Google Wallet with one scan, then earn stamps, points,
                    tiers, and cashback every time your staff scan at the
                    counter—no app to download, no paper cards to lose, and
                    balances that update live on every visit.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href="https://scan4loyalty.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary text-sm px-6 text-center"
                    >
                      Go to scan4loyalty.com
                    </a>
                    <a
                      href="mailto:sales@scan4loyalty.com?subject=Scan4Loyalty%20%E2%80%94%20Sales%20inquiry"
                      className="btn-secondary text-sm px-6 text-center"
                    >
                      Talk to Sales
                    </a>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="glass-panel rounded-2xl p-6 lg:p-8 shadow-glow-strong lg:sticky lg:top-28"
                >
                  <p className="text-sm font-medium text-neon-300 mb-4">
                    Loyalty. In your mobile wallet. Always.
                  </p>
                  <ul className="space-y-3 text-sm text-gray-300">
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">
                          Wallet-native passes
                        </strong>{' '}
                        — Apple Wallet and Google Wallet, with lock-screen
                        offers and reminders
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">
                          Live balance, no reissue
                        </strong>{' '}
                        — every award or redeem updates the pass immediately
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-primary-400 shrink-0">✓</span>
                      <span>
                        <strong className="text-white">
                          Works across locations
                        </strong>{' '}
                        — network redemption lets separate companies or
                        locations honor the same pass
                      </span>
                    </li>
                  </ul>
                </motion.div>
              </div>

              <div className="mt-10 lg:mt-12">
                <p className="eyebrow mb-2">How it works</p>
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Simple for You. Valuable for Your Customers.
                </h4>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {[
                    {
                      title: 'Scan to Join',
                      body: 'Customers scan a QR code at your counter to instantly join your program.',
                    },
                    {
                      title: 'Added to Wallet in One Tap',
                      body: 'The loyalty pass lands in Apple Wallet or Google Wallet—nothing to install.',
                    },
                    {
                      title: 'Engage & Notify',
                      body: 'Reach members with offers and reminders right on their lock screen.',
                    },
                    {
                      title: 'Earn & Redeem with a Scan',
                      body: 'Staff scan the member’s pass to award or redeem stamps and points in seconds.',
                    },
                    {
                      title: 'Track & Grow',
                      body: 'See real-time insights on visits, engagement, and revenue.',
                    },
                    {
                      title: 'Network Redemption',
                      body: 'Let partner businesses opt in to honor the same pass—share loyalty, grow together.',
                    },
                  ].map(({ title, body }) => (
                    <div
                      key={title}
                      className="rounded-xl border border-white/10 bg-surface-300/25 p-5"
                    >
                      <p className="font-display text-sm font-semibold text-white">
                        {title}
                      </p>
                      <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                        {body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 pt-10 border-t border-white/10">
                <p className="eyebrow mb-2">Program types</p>
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Built Past the Basic Stamp Card
                </h4>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-gray-400">
                  {[
                    {
                      name: 'Stamp Card',
                      body: 'Buy N, get one free. Stamps collect on the pass.',
                    },
                    {
                      name: 'Points',
                      body: 'Earn points per spend and redeem at a threshold you set.',
                    },
                    {
                      name: 'Points + Tier',
                      body: 'Points for everyday rewards, plus Bronze / Silver / Gold style perks.',
                    },
                    {
                      name: 'Tiered VIP',
                      body: 'Named tiers with perks. Members see their status on the pass they already open.',
                    },
                    {
                      name: 'Cashback',
                      body: 'A percentage back as wallet credit after qualifying visits.',
                    },
                  ].map(({ name, body }) => (
                    <li key={name} className="flex gap-2 items-start">
                      <span className="text-primary-400 shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>
                        <strong className="text-white font-semibold">
                          {name}
                        </strong>{' '}
                        — {body}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 pt-10 border-t border-white/10">
                <p className="eyebrow mb-2">Use cases</p>
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Built for the Counter You Run
                </h4>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-400 list-disc list-inside marker:text-primary-400">
                  {[
                    'Cafes & coffee shops',
                    'Restaurants & QSRs',
                    'Retail & boutiques',
                    'Gyms & fitness studios',
                    'Salons & spas',
                    'Hotels & resorts',
                  ].map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 rounded-xl border border-primary-500/25 bg-primary-950/30 p-6 lg:p-8">
                <h4 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">
                  Ready to Replace Paper Cards?
                </h4>
                <p className="mt-3 text-sm text-gray-300 leading-relaxed max-w-3xl">
                  Tell us about the counter you run and we will help you launch
                  a wallet-based loyalty program your customers actually use.
                </p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-3 text-sm text-gray-400">
                  {[
                    '5 program types to fit your business',
                    'Apple Wallet & Google Wallet passes',
                    'Zero apps to download',
                    'Balance updates live on every scan',
                  ].map(text => (
                    <li key={text} className="flex gap-2 items-start">
                      <span className="text-primary-400 shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          </div>
        </section>

        <section
          id="products"
          className="py-16 lg:py-24 scroll-mt-24 border-b border-white/5"
        >
          <div className="container-custom">
            <motion.div {...fadeUp} className="max-w-3xl mb-12">
              <p className="eyebrow mb-3">Products</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                What we ship today
              </h2>
            </motion.div>

            <motion.article
              {...fadeUp}
              className="mb-14 rounded-2xl border-2 border-primary-500/40 bg-gradient-to-br from-primary-950/50 to-surface-200/40 p-8 lg:p-10"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="max-w-3xl">
                  <h3 className="m-0 font-normal text-left">
                    <img
                      src="/assets/images/documint-logo.png"
                      alt="Documint"
                      className="h-9 sm:h-10 w-auto max-w-[240px] object-contain object-left"
                      width="240"
                      height="44"
                    />
                  </h3>
                  <span className="inline-block mt-3 rounded-full bg-primary-500/25 px-3 py-1 text-xs font-semibold text-primary-100">
                    Flagship platform · documint.id
                  </span>
                  <p className="mt-3 text-gray-300 leading-relaxed">
                    Documint is our self-sovereign identity platform: turn
                    diplomas, certificates, and professional credentials into
                    cryptographically verifiable digital assets. Built on W3C
                    Verifiable Credentials, DIDs, and OpenID4VC-style flows so
                    issuers, holders, and verifiers can trust outcomes without
                    endless manual checks—aligned with modern India DPI–style
                    trust infrastructure where applicable.
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-gray-400 list-disc list-inside marker:text-primary-400">
                    <li>
                      Issue, hold, and verify credentials with instant
                      validation
                    </li>
                    <li>
                      Reduce credential fraud and strengthen institutional trust
                    </li>
                    <li>
                      Designed for universities, enterprises, and public-sector
                      ecosystems
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3 shrink-0">
                  <a
                    href="https://documint.id/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary text-center px-8"
                  >
                    Go to documint.id
                  </a>
                  <Link to="/contact" className="btn-secondary text-center">
                    Ask about Documint
                  </Link>
                </div>
              </div>
            </motion.article>

            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.03 }}
              className="mb-10 rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-950/35 to-surface-200/45 p-8 lg:p-10"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="max-w-3xl">
                  <div className="inline-flex max-w-[1100px] rounded-2xl bg-white px-4 py-1 shadow-lg shadow-black/20">
                    <img
                      src="/assets/images/idgate-logo.png"
                      alt="IDGate Check-In"
                      className="block h-60 w-auto max-w-[1100px] object-contain object-left"
                      width="1100"
                      height="240"
                    />
                  </div>
                  <span className="inline-block mt-3 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-100">
                    Secure visitor &amp; access operations
                  </span>
                  <p className="mt-4 text-gray-300 leading-relaxed">
                    IDGate Check-In helps organizations verify identities,
                    streamline visitor movement, and strengthen front-desk
                    security—with real-time Aadhaar verification, paperless
                    registration, and centralized monitoring for a faster, safer
                    arrival experience.
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-gray-400 list-disc list-inside marker:text-primary-400">
                    <li>
                      Identity-first check-in with stronger assurance at every
                      entry
                    </li>
                    <li>
                      Frictionless arrival: digital registration and shorter
                      queues
                    </li>
                    <li>
                      Governed operations: visitor visibility, audit trails, and
                      compliance-ready controls
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3 shrink-0">
                  <Link
                    to="/contact"
                    className="btn-primary text-center px-8 text-sm"
                  >
                    Schedule a Demo
                  </Link>
                  <a
                    href="mailto:support@latchiya.com?subject=IDGate%20%E2%80%94%20Sales%20inquiry"
                    className="btn-secondary text-center text-sm"
                  >
                    Talk to Sales
                  </a>
                </div>
              </div>
            </motion.article>

            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.03 }}
              className="mb-10 rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-950/35 to-surface-200/45 p-8 lg:p-10"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="max-w-3xl">
                  <div className="inline-flex rounded-2xl bg-white px-6 py-4 shadow-lg shadow-black/20">
                    <img
                      src="/assets/images/scan4loyalty-logo.png"
                      alt="Scan4Loyalty"
                      className="block h-24 sm:h-28 w-auto object-contain object-left"
                      width="900"
                      height="398"
                    />
                  </div>
                  <span className="inline-block mt-3 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-100">
                    Loyalty in your mobile wallet · scan4loyalty.com
                  </span>
                  <p className="mt-4 text-gray-300 leading-relaxed">
                    Scan4Loyalty issues loyalty passes to Apple Wallet and
                    Google Wallet. Members scan at the counter for stamps,
                    points, tiers, wallets, and cashback—no app to download, and
                    balances update live on every scan.
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-gray-400 list-disc list-inside marker:text-primary-400">
                    <li>
                      Stamp Card, Points, Points + Tier, Tiered VIP, and
                      Cashback programs
                    </li>
                    <li>Counter QR scan for fast, staff-friendly checkout</li>
                    <li>
                      Cross-company network redemption across participating
                      businesses
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3 shrink-0">
                  <a
                    href="https://scan4loyalty.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary text-center px-8 text-sm"
                  >
                    Go to scan4loyalty.com
                  </a>
                  <a
                    href="mailto:sales@scan4loyalty.com?subject=Scan4Loyalty%20%E2%80%94%20Sales%20inquiry"
                    className="btn-secondary text-center text-sm"
                  >
                    Talk to Sales
                  </a>
                </div>
              </div>
            </motion.article>

            <div className="grid gap-10 lg:grid-cols-2">
              <motion.article
                {...fadeUp}
                className="flex flex-col rounded-2xl border border-neon-500/25 bg-gradient-to-b from-neon-900/15 to-surface-200/40 p-6 lg:p-8"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex rounded-md bg-white p-1.5 shrink-0">
                    <img
                      src="/assets/images/m-Reach_std.png"
                      alt=""
                      className="h-10 w-auto object-contain"
                    />
                  </span>
                  <h3 className="font-display text-xl font-bold text-white">
                    m-REACH
                  </h3>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Web-based, self-service SMS platform for operators to offer
                  messaging to SMEs, and for businesses to run mobile campaigns.
                  Organizations use keywords, voice calls, or web widgets so
                  people opt in to a distribution list; then they run mobile
                  coupons, reminders, subscription flows, voice broadcast,
                  mobile surveys, voting, quizzes, and more—without heavy manual
                  steps. Strength is generating tailored SMS from enterprise
                  systems and delivering to recipients reliably.
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-gray-400 list-disc list-inside marker:text-neon-400">
                  <li>Two-way messaging and affordable campaign execution</li>
                  <li>
                    Integrates with business systems for vertical-specific
                    mobility
                  </li>
                  <li>
                    Multi-channel campaigns (SMS, voice, web entry points)
                  </li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href="https://www.m-reach.com"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary text-sm"
                  >
                    m-reach.com
                  </a>
                  <Link to="/contact" className="btn-secondary text-sm">
                    Pilot m-REACH
                  </Link>
                </div>
              </motion.article>

              <motion.article
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.05 }}
                className="flex flex-col rounded-2xl border border-white/10 bg-surface-200/50 p-6 lg:p-8"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex rounded-md bg-white p-1.5 shrink-0">
                    <img
                      src="/assets/images/eclassroomz_std.png"
                      alt=""
                      className="h-10 w-auto object-contain"
                    />
                  </span>
                  <h3 className="font-display text-xl font-bold text-white">
                    e-Classroomz
                  </h3>
                </div>
                <div className="space-y-4 text-sm text-gray-300 leading-relaxed">
                  <p>
                    With{' '}
                    <strong className="text-white font-semibold">
                      e-Classroomz
                    </strong>
                    , a collaboration portal for students and teachers, you can
                    take your educational institution to the next level of being
                    socially mobile.
                  </p>
                  <p>
                    Get a{' '}
                    <strong className="text-gray-200">dynamic website</strong>{' '}
                    for every physical classroom in your institution—from
                    distance and hybrid programs to face-to-face campuses—with
                    course sites, project sites, calendars, and aggregate views
                    across groups.
                  </p>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                      What you can do
                    </p>
                    <ul className="space-y-2 text-sm text-gray-400 list-none pl-0">
                      {[
                        'Make class announcements',
                        'Keep a class calendar of deadlines and activities',
                        'Conduct quizzes or tests',
                        'Post and view a syllabus',
                        'Manage, store, organize, and share class materials and media with class members',
                        'Manage student grades',
                        'Provide targeted feedback on assignments or lessons',
                        'Moderate and participate in class discussions',
                        'Send private messages to individual students',
                        'Send messages to all students in a class',
                      ].map(text => (
                        <li key={text} className="flex gap-2 items-start">
                          <span className="text-primary-400 shrink-0 mt-0.5">
                            ✓
                          </span>
                          <span>{text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p>
                    <a
                      href="https://eclassroomz.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-300 hover:text-primary-200 underline-offset-2 hover:underline"
                    >
                      Explore e-Classroomz
                    </a>{' '}
                    for a full walkthrough of the platform.
                  </p>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href="https://eclassroomz.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary text-sm"
                  >
                    eclassroomz.com
                  </a>
                  <Link to="/contact" className="btn-secondary text-sm">
                    Pilot eClassroomz
                  </Link>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        <section
          id="free-pilot"
          className="py-16 lg:py-24 scroll-mt-24 border-b border-white/5"
        >
          <div className="container-custom">
            <div className="glass-panel rounded-2xl p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                <p className="eyebrow mb-3">Free pilot</p>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
                  Start with a scoped pilot or consulting sprint
                </h2>
                <p className="mt-4 text-gray-400 leading-relaxed">
                  Tell us whether you are evaluating Documint, m-REACH,
                  eClassroomz, or IDGate—or need integration help. Use the
                  contact form so your message reaches{' '}
                  <strong className="text-gray-300">
                    support@latchiya.com
                  </strong>{' '}
                  with full context.
                </p>
              </div>
              <Link
                to="/contact"
                className="btn-primary text-center shrink-0 px-8"
              >
                Open contact form
              </Link>
            </div>
          </div>
        </section>

        <section
          id="careers"
          className="py-16 lg:py-24 scroll-mt-24 border-b border-white/5 bg-surface-200/20"
        >
          <div className="container-custom">
            <motion.div {...fadeUp} className="max-w-3xl">
              <p className="eyebrow mb-3">Careers</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                Product &amp; Field Sales Roles
              </h2>
              <p className="mt-4 text-gray-300 leading-relaxed">
                We&apos;re hiring people who want to sell real, production-ready
                software—not presentations. Our teams work directly with
                customers across{' '}
                <strong className="text-gray-200">Documint</strong>,{' '}
                <strong className="text-gray-200">m-REACH</strong>,{' '}
                <strong className="text-gray-200">eClassroomz</strong>, and{' '}
                <strong className="text-gray-200">IDGate</strong>, driving
                adoption through hands-on engagement and execution.
              </p>
              <p className="mt-4 text-gray-400 leading-relaxed">
                This is a field sales role. You will be responsible for
                demonstrating products, generating leads, and converting them
                into paying customers. Domestic travel is a core part of the
                job.
              </p>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 }}
              className="mt-10 max-w-3xl rounded-2xl border border-white/10 bg-surface-300/30 p-6 lg:p-8"
            >
              <h3 className="font-display text-xl font-semibold text-white">
                Senior Sales Executive
              </h3>

              <p className="mt-6 text-sm font-semibold text-white">
                What we&apos;re looking for:
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-400">
                {[
                  'Proven experience selling software products',
                  'Strong communication and negotiation skills in English',
                  'Ability to understand product workflows and clearly demonstrate value to customers',
                  'Experience in field sales: product demos, lead generation, and deal closure',
                  'Confidence working with both technical teams and decision-makers',
                  'Willingness to travel domestically',
                ].map(text => (
                  <li key={text} className="flex gap-2 items-start">
                    <span className="text-primary-400 shrink-0 mt-0.5">✓</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-8 text-sm font-semibold text-white">
                Preferred background:
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-400">
                {[
                  'Degree in Engineering, Technology, and/or MBA',
                  'Experience selling and closing software deals in B2B environments',
                ].map(text => (
                  <li key={text} className="flex gap-2 items-start">
                    <span className="text-primary-400 shrink-0 mt-0.5">✓</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-8 text-sm text-gray-500">
                Apply at:{' '}
                <a
                  href="mailto:careers@latchiya.com?subject=Careers%20%E2%80%94%20Senior%20Sales%20Executive"
                  className="text-primary-300 hover:text-primary-200 underline-offset-2 hover:underline"
                >
                  careers@latchiya.com
                </a>
              </p>
            </motion.div>
          </div>
        </section>

        <section
          id="management"
          className="py-16 lg:py-24 scroll-mt-24 border-b border-white/5"
        >
          <div className="container-custom">
            <motion.div {...fadeUp} className="max-w-3xl mb-10 lg:mb-12">
              <p className="eyebrow mb-3">Management</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight">
                Leadership
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Latchiya is led by founders with deep, hands-on engineering and
                product experience.
              </p>
            </motion.div>
            <div className="grid gap-8 lg:grid-cols-2">
              <motion.article
                {...fadeUp}
                className="rounded-2xl border border-white/10 bg-surface-200/40 flex flex-col sm:flex-row sm:items-start gap-6 p-6 lg:p-8"
              >
                <div className="flex justify-center sm:justify-start shrink-0">
                  <div className="relative h-40 w-40 sm:h-44 sm:w-44 overflow-hidden rounded-full bg-surface-300/50 ring-2 ring-white/15 shadow-lg">
                    <img
                      src="/assets/images/founder-kodiswaran-babu.png"
                      alt="Kodiswaran Babu Janardhanan"
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{
                        objectPosition: '50% 16%',
                        transform: 'scale(1.72)',
                        transformOrigin: '50% 26%',
                      }}
                      width="320"
                      height="400"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-bold text-white">
                    Kodiswaran Babu Janardhanan
                  </h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                    Kodiswaran Babu Janardhanan, Founder of Latchiya, brings
                    over 3 decades of experience across the full software
                    stack—from low-level systems to modern AI-driven
                    applications. His background spans Qualcomm (9+ years across
                    mobile and base station systems, including kernel leadership
                    during 1G–4G evolution), Motorola (MMS architecture), Intel
                    (wearables, IoT), Aricent, and Fidelity Investments
                    (Director of Architecture &amp; Strategy). He has also led
                    work in blockchain, crypto, and GameFi as CTO of a crypto
                    gaming platform, and now focuses on decentralized identity,
                    cryptography, and AI.
                  </p>
                  <p className="mt-4">
                    <a
                      href="https://www.linkedin.com/in/kodis"
                      className="text-sm text-primary-300 hover:text-primary-200 underline-offset-2 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn profile
                    </a>
                  </p>
                </div>
              </motion.article>
              <motion.article
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.05 }}
                className="rounded-2xl border border-white/10 bg-surface-200/40 flex flex-col sm:flex-row sm:items-start gap-6 p-6 lg:p-8"
              >
                <div className="flex justify-center sm:justify-start shrink-0">
                  <div className="relative h-40 w-40 sm:h-44 sm:w-44 overflow-hidden rounded-full bg-surface-300/50 ring-2 ring-white/15 shadow-lg">
                    <img
                      src="/assets/images/founder-thanuja-kodiswaran.png"
                      alt="Thanuja Ramiya"
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{
                        objectPosition: '50% 12%',
                        transform: 'translate(-10%, -15%) scale(2.55)',
                        transformOrigin: '50% 20%',
                      }}
                      width="320"
                      height="400"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-bold text-white">
                    Thanuja Ramiya
                  </h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                    Thanuja Ramiya, Co-Founder, brings strong engineering depth
                    from Qualcomm and a computer science background from Madurai
                    Kamaraj University. She drives execution and ensures the
                    same rigor in how products are built, shipped, and scaled.
                  </p>
                  <p className="mt-4">
                    <a
                      href="https://www.linkedin.com/in/thanuja-kodis-1290b013/"
                      className="text-sm text-primary-300 hover:text-primary-200 underline-offset-2 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn profile
                    </a>
                  </p>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        <section
          id="company"
          className="py-16 lg:py-24 border-b border-white/5 bg-surface-200/30 scroll-mt-24"
        >
          <div className="container-custom">
            <motion.div {...fadeUp} className="max-w-3xl">
              <p className="eyebrow mb-3">Company</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight">
                About Latchiya
              </h2>
              <div className="mt-6 space-y-5 text-gray-300 leading-relaxed">
                <p>
                  <strong className="text-white font-semibold">
                    Latchiya Software Systems
                  </strong>{' '}
                  is an AI-first product company building global-scale digital
                  platforms where trust is built in by design—not added later.
                  We focus on self-sovereign identity (SSI), blockchain-based
                  assurance, and cryptographic verification, enabling
                  credentials and relationships to be trusted across systems,
                  organizations, and borders without compromising privacy.
                </p>
                <p>
                  Our flagship platform,{' '}
                  <a
                    href="https://documint.id/"
                    className="text-primary-300 hover:text-primary-200 underline-offset-2 hover:underline font-medium"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Documint
                  </a>
                  , provides decentralized identity and verifiable credentials
                  infrastructure aligned with W3C standards (Verifiable
                  Credentials, DIDs) and OpenID4VC flows—built for real-world
                  enterprise and digital public infrastructure use cases.
                </p>
                <p>
                  <strong className="text-gray-200">IDGate Check-In</strong>{' '}
                  transforms the arrival experience into a fast, secure, and
                  intelligently verified workflow. With built-in Aadhaar
                  verification, it enables real-time identity confirmation while
                  streamlining visitor registration, check-in, and access
                  control in a single system. The result is a smoother guest
                  experience, stronger compliance, and greater operational
                  confidence for teams that cannot afford delays or uncertainty.
                </p>
                <p>
                  <strong className="text-gray-200">IDGate Check-In</strong> is
                  especially effective across hospitals, corporate offices,
                  coworking spaces, manufacturing plants, warehouses,
                  educational campuses, residential communities, government
                  facilities, and events—anywhere trusted identity validation
                  and efficient entry management are critical.
                </p>
                <p>
                  In addition, we support organizations with integration,
                  deployment, and production hardening of these systems.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
